/**
 * 动画演示教程的脚本数据。
 * 每一步：标题 / 说明 / 提示 / 一个声明式的界面示意图 (screen) / 可跳转的页面。
 *
 * screen.kind:
 *   form    — 字段名 + 值的表单示意，hit=true 的字段高亮
 *   table   — 表头 + 若干行，hit 行高亮
 *   buttons — 一排按钮/标签
 *   banner  — 绿色提示条（如历史最低价）
 *   cards   — 方块卡片（如报价单选择器）
 */
export const TUTORIALS = (lang) => {
  const zh = lang === "zh";
  return {
    quotes: {
      title: zh ? "动画演示：从新建报价单到发给客户" : "Walkthrough: from a new quote to the customer",
      route: "/quotes",
      steps: [
        {
          title: zh ? "新建报价单" : "Create the quote",
          desc: zh
            ? "「报价单」页右上角点新建，填报价单号、选客户、写项目名。客户下拉来自客户管理，选了客户就带出它的货币和价格系数。"
            : "On the Quotes page click New: quote number, customer, project. The customer dropdown comes from Customers and carries its currency and multiplier.",
          hint: zh ? "报价单号建议用「客户缩写 + 年月 + 序号」，方便日后检索。" : "Use 'customer + yy/mm + seq' for quote numbers so they're easy to search later.",
          route: "/quotes",
          screen: {
            kind: "form", title: zh ? "新建报价单" : "New quote",
            items: [
              { k: zh ? "报价单号" : "Quote no.", v: "GT-2601", hit: true },
              { k: zh ? "客户" : "Customer", v: "ALPEMUSA" },
              { k: zh ? "项目" : "Project", v: zh ? "2026 春季床品" : "SS26 Bedding" },
            ],
          },
        },
        {
          title: zh ? "设置报价汇率" : "Set the quote exchange rate",
          desc: zh
            ? "单头的「报价汇率」决定美元价 = 人民币价 ÷ 汇率。留空则用系统设置里的默认汇率。"
            : "The header's exchange rate drives USD price = RMB price ÷ rate. Leave it blank to use the system default.",
          hint: zh ? "同一张报价单只有一个汇率；汇率一改，整张单的计算价会立刻重算。" : "One rate per quote — changing it recomputes every line instantly.",
          route: "/quotes",
          screen: {
            kind: "form", title: zh ? "客户信息" : "Customer summary",
            items: [
              { k: zh ? "货币" : "Currency", v: "USD" },
              { k: zh ? "海运费" : "Freight", v: "0.35" },
              { k: zh ? "报价汇率" : "Exchange rate", v: "6.70", hit: true },
            ],
          },
        },
        {
          title: zh ? "把产品加进来" : "Add products",
          desc: zh
            ? "右上角购物车把这张单设为「当前活跃报价单」，然后去产品页勾选产品点「加入报价单」；也可以在报价单里直接搜货号添加。"
            : "Use the header cart to make this the active quote, then tick products on the Products page and click Add to quote. You can also search by item number inside the quote.",
          hint: zh ? "勾选状态跨页保留 24 小时，可以分几次慢慢挑。" : "Selections persist across pages for 24 hours, so you can pick in several passes.",
          route: "/products",
          screen: {
            kind: "cards",
            title: zh ? "选择报价单" : "Pick a quote",
            items: ["GT-2601", "GT-2555", zh ? "+ 新建" : "+ New"],
          },
        },
        {
          title: zh ? "逐尺寸核对价格" : "Check prices per size",
          desc: zh
            ? "点产品行的编辑图标进入明细：每个尺寸一行，核对工厂成本、关税、装箱数据，售价由公式自动算出。"
            : "Open a row's editor: one line per size — check factory cost, duty and carton data; selling prices are computed by formula.",
          hint: zh ? "表头可以拖动排序，顺序会记在你自己的电脑上。" : "Drag the table headers to reorder columns; the layout is remembered per device.",
          route: "/quotes",
          screen: {
            kind: "table",
            head: [zh ? "尺寸" : "Size", zh ? "工厂成本" : "Cost", zh ? "装箱" : "Carton", zh ? "报价" : "Price"],
            rows: [
              ["QUEEN", "¥17.51", "12P 48×30×32", "$3.15", ],
              ["KING", "¥19.80", "12P 48×30×38", "$3.56"],
            ],
            hit: 0,
          },
        },
        {
          title: zh ? "补装箱数据（可选）" : "Fill carton data (optional)",
          desc: zh
            ? "产品行右侧的箱子图标打开「订单产品资料」，从历史合同里一键导入装箱率、长宽高、毛重和 40HQ 装柜量。"
            : "The carton icon on each row opens Order Product Data: import packing rate, L/W/H, gross weight and 40HQ quantity from past contracts in one click.",
          hint: zh ? "可以顺便勾选「同时把采购价导入工厂成本」。" : "You can also tick 'also import the purchase price into factory cost'.",
          route: "/quotes",
          screen: {
            kind: "banner",
            text: zh ? "历史最低价 ¥17.51 · 盐城泰诚 · 5M0214 · 2026-04-15" : "Lowest ever ¥17.51 · Yancheng Taicheng · 5M0214 · 2026-04-15",
          },
        },
        {
          title: zh ? "共享给同事" : "Share with colleagues",
          desc: zh
            ? "在单头编辑里把同事邮箱加入共享名单，分为只读或可编辑。销售经理默认能看到全组报价单。"
            : "In the header editor add teammate emails as read-only or editable. Sales managers see the whole team's quotes by default.",
          hint: zh ? "共享不发文件，对方在自己的报价单列表里就能看到。" : "No files are sent — the quote simply appears in their own list.",
          route: "/quotes",
          screen: { kind: "buttons", items: [zh ? "只读" : "Read", zh ? "可编辑" : "Edit", "sm@…", "pmgr@…"] },
        },
        {
          title: zh ? "导出并发给客户" : "Export and send",
          desc: zh
            ? "报价单顶部一键导出 Excel、PDF、打印标签，或直接发 HTML 邮件给客户。"
            : "From the top of the quote export Excel, PDF, printed labels, or email the HTML quote directly.",
          hint: zh ? "导出时价格会被锁定留档，之后成本变动不会影响已发出的报价。" : "Prices are frozen on export, so later cost changes never alter what you already sent.",
          route: "/quotes",
          screen: { kind: "buttons", items: ["Excel", "PDF", zh ? "邮件" : "Email", zh ? "标签" : "Labels"] },
        },
      ],
    },

    procurement: {
      title: zh ? "动画演示：填写一条采购报价" : "Walkthrough: filling a procurement quote",
      route: "/procurement-quotes",
      steps: [
        {
          title: zh ? "新建记录" : "Create the record",
          desc: zh
            ? "「采购报价」页点新建，或在列表里找到同款记录点「复制为新记录」——复制会保留内容并把日期设为今天。"
            : "Click New on the Procurement Quotes page, or find a similar record and use Save as copy — it keeps the content and sets today's date.",
          hint: zh ? "复制后窗体会停在新记录上，可以直接接着改。" : "After copying, the form stays on the new record so you can keep editing.",
          route: "/procurement-quotes",
          screen: {
            kind: "form", title: zh ? "基本信息" : "Basics",
            items: [
              { k: zh ? "产品编号" : "Item no.", v: "NL17717", hit: true },
              { k: zh ? "产品名称" : "Name", v: zh ? "超柔床单四件套" : "Microfiber sheet set" },
              { k: zh ? "工厂" : "Factory", v: zh ? "盐城泰诚" : "Yancheng Taicheng" },
            ],
          },
        },
        {
          title: zh ? "填价格信息" : "Enter the pricing block",
          desc: zh
            ? "每米价、用料尺寸、加工费、辅料费、运费、利润率与税率填好后，系统自动算出这条报价的价格。"
            : "Metre price, fabric size, labour, trims, freight, margin and tax — the price is computed automatically.",
          hint: zh ? "价格是公式算出来的只读字段，改上面的参数就会立刻变。" : "The price is a read-only formula result; change the inputs and it updates live.",
          route: "/procurement-quotes",
          screen: {
            kind: "form", title: zh ? "价格信息" : "Pricing",
            items: [
              { k: zh ? "每米价" : "Metre price", v: "¥7.80" },
              { k: zh ? "加工费" : "Labour", v: "¥2.10" },
              { k: zh ? "价格" : "Price", v: "¥17.51", hit: true },
            ],
          },
        },
        {
          title: zh ? "看历史最低价" : "Check the lowest ever price",
          desc: zh
            ? "窗体里的「订单产品资料」面板会自动按货号匹配历史合同，顶部绿条直接显示历史最低采购价、工厂、合同号和日期。"
            : "The Order Product Data panel matches past contracts by item number; the green bar shows the lowest historical price with factory, contract and date.",
          hint: zh ? "价格旁的红▲绿▼是与当前价格的涨跌幅，谈价时很有用。" : "The red ▲ / green ▼ shows the change against your current price — handy when negotiating.",
          route: "/procurement-quotes",
          screen: {
            kind: "banner",
            text: zh ? "历史最低价 ¥17.51 · 盐城泰诚 · 5M0214 · 26 笔含价合同中最低" : "Lowest ever ¥17.51 · Yancheng Taicheng · 5M0214 · lowest of 26 priced contracts",
          },
        },
        {
          title: zh ? "导入装箱数据" : "Import the carton data",
          desc: zh
            ? "在面板里点某行「导入」，或点「导入订单产品资料」打开弹窗搜索筛选后再导入：装箱率、长、宽、高、毛重、40HQ 装柜量一次填好。"
            : "Click Import on a row, or open the dialog to search and filter first: packing rate, L/W/H, gross weight and 40HQ quantity are filled at once.",
          hint: zh ? "弹窗可搜中英文名称、货号、合同号、厂家，并按合同号/客户/厂家筛选。" : "The dialog searches CN/EN names, item and contract numbers, and filters by contract, customer and factory.",
          route: "/procurement-quotes",
          screen: {
            kind: "table",
            head: [zh ? "合同号" : "Contract", zh ? "采购价" : "Price", zh ? "装箱信息" : "Carton", ""],
            rows: [
              ["5M0214", "¥17.51", "12P 48×30×32", zh ? "导入" : "Import"],
              ["5M0311", "¥19.20", "12P 48×30×38", zh ? "导入" : "Import"],
            ],
            hit: 0,
          },
        },
        {
          title: zh ? "核对 QTY HQ 与毛重" : "Verify QTY HQ and gross weight",
          desc: zh
            ? "QTY HQ 会按箱规和装箱率自动算出，也可以手动改；毛重直接填实测值。"
            : "QTY HQ is calculated from carton size and packing rate but can be overridden; enter the measured gross weight.",
          hint: zh ? "箱规任一项为 0 时 QTY HQ 算不出来，会显示 0。" : "If any carton dimension is 0, QTY HQ can't be computed and shows 0.",
          route: "/procurement-quotes",
          screen: {
            kind: "form", title: zh ? "装箱数据" : "Carton data",
            items: [
              { k: zh ? "装箱率" : "Packing rate", v: "12" },
              { k: "QTY HQ", v: "16,927", hit: true },
              { k: zh ? "毛重" : "Gross weight", v: "10 kg" },
            ],
          },
        },
        {
          title: zh ? "保存 · 翻页 · 查历史" : "Save · walk records · history",
          desc: zh
            ? "保存后可用「上一个 / 下一个」连续处理多条；操作列的历史图标能看到同一货号的所有版本。"
            : "After saving use Prev / Next to keep going; the history icon shows every version of that item number.",
          hint: zh ? "每次编辑前系统都会存一份快照，改错了可以对照恢复。" : "A snapshot is stored before each edit, so mistakes can be compared and recovered.",
          route: "/procurement-quotes",
          screen: { kind: "buttons", items: [zh ? "‹ 上一个" : "‹ Prev", zh ? "保存" : "Save", zh ? "存为副本" : "Save as copy", zh ? "下一个 ›" : "Next ›"] },
        },
      ],
    },

    "qr-share": {
      title: zh ? "动画演示：采购收到链接后怎么填报价" : "Walkthrough: a buyer filling a quote from a link",
      route: "/quotes",
      steps: [
        {
          title: zh ? "外销生成链接" : "The salesperson creates the link",
          desc: zh
            ? "在报价单页面为某个采购生成专属链接，链接只包含分配给他的产品，并带有效期。"
            : "From the quote page, generate a link for a specific buyer. It only contains that buyer's products and carries an expiry date.",
          hint: zh ? "链接可以随时作废重新生成，作废后旧链接立即失效。" : "Links can be revoked and regenerated at any time; the old link dies immediately.",
          route: "/quotes",
          screen: { kind: "buttons", items: [zh ? "生成链接" : "Create link", zh ? "复制" : "Copy", zh ? "作废" : "Revoke"] },
        },
        {
          title: zh ? "采购打开链接并登录" : "The buyer opens it and signs in",
          desc: zh
            ? "采购点开链接，如果还没登录会先要求登录；登录完成后**直接落在**要填报的页面，不会跑回首页。"
            : "If not signed in, the buyer is asked to log in first — and lands **straight** on the page to fill in, not on the home page.",
          hint: zh ? "第一次使用请提醒采购先改密码。" : "Remind first-time buyers to change their password.",
          route: "/login",
          screen: {
            kind: "form", title: zh ? "登录" : "Sign in",
            items: [
              { k: zh ? "邮箱" : "Email", v: "buyer@…" },
              { k: zh ? "密码" : "Password", v: "••••••", hit: true },
            ],
          },
        },
        {
          title: zh ? "只看到自己的产品" : "Only their own products",
          desc: zh
            ? "页面顶部显示报价单号与截止日期，下面是分配给这位采购的产品清单，其他人的产品看不到。"
            : "The header shows the quote number and due date; below it are only the products assigned to that buyer.",
          hint: zh ? "如果清单是空的，说明还没有把产品的「采购」设成他。" : "An empty list means no product has that person set as the buyer yet.",
          route: "/quotes",
          screen: {
            kind: "table",
            head: [zh ? "货号" : "Item", zh ? "名称" : "Name", zh ? "状态" : "Status"],
            rows: [["NL17717", zh ? "超柔床单" : "Sheet set", zh ? "待填报" : "To fill"], ["XM20-TB05", zh ? "桌布" : "Table cloth", zh ? "待填报" : "To fill"]],
            hit: 0,
          },
        },
        {
          title: zh ? "填写采购报价" : "Fill in the quote",
          desc: zh
            ? "点产品进入采购报价窗体，填价格三块与装箱数据，可以直接导入历史合同的装箱数据。"
            : "Open a product to get the procurement form: the three pricing blocks and carton data, with one-click import from past contracts.",
          hint: zh ? "填完保存即可，外销那边立刻能看到。" : "Just save — the salesperson sees it immediately.",
          route: "/procurement-quotes",
          screen: {
            kind: "form", title: zh ? "价格信息" : "Pricing",
            items: [
              { k: zh ? "每米价" : "Metre price", v: "¥7.80", hit: true },
              { k: zh ? "价格" : "Price", v: "¥17.51" },
            ],
          },
        },
        {
          title: zh ? "外销核对并锁价" : "The salesperson reviews and locks",
          desc: zh
            ? "外销在报价单里看到采购填的成本，核对后导出给客户，导出即锁价留档。"
            : "The salesperson sees the submitted costs in the quote, reviews them, then exports to the customer — which freezes the prices.",
          hint: zh ? "采购经理可以查看并编辑全部采购报价，用于复核。" : "Procurement managers can review and edit every procurement quote.",
          route: "/quotes",
          screen: { kind: "buttons", items: ["Excel", "PDF", zh ? "邮件" : "Email"] },
        },
      ],
    },

    products: {
      title: zh ? "动画演示：录入一个新产品" : "Walkthrough: entering a new product",
      route: "/products",
      steps: [
        {
          title: zh ? "新建产品" : "Create the product",
          desc: zh
            ? "产品页顶部点 + 号，填货号（最多 15 位）、中英文名称、类别与品牌。"
            : "Click + at the top of the Products page: item number (max 15 chars), Chinese and English name, category and brand.",
          hint: zh ? "外销新建时「外销」会自动填成自己，采购新建时「采购」自动填自己。" : "The seller / buyer field is prefilled with your own name depending on your role.",
          route: "/products/new",
          screen: {
            kind: "form", title: zh ? "基本信息" : "Basics",
            items: [
              { k: zh ? "货号" : "Item no.", v: "NL17999", hit: true },
              { k: zh ? "中文名称" : "Name (zh)", v: zh ? "水洗棉四件套" : "Washed cotton set" },
              { k: zh ? "类别" : "Category", v: "BEDDING" },
            ],
          },
        },
        {
          title: zh ? "上传产品图片" : "Upload the photos",
          desc: zh
            ? "左侧图片区上传产品图和花型图，系统自动生成 4 种尺寸并存到云端。"
            : "Upload the main photo and the pattern photo on the left; four sizes are generated and stored in the cloud automatically.",
          hint: zh ? "超过 2MB 会自动压缩，清晰度基本不受影响。" : "Files over 2 MB are compressed automatically with almost no visible loss.",
          route: "/products",
          screen: { kind: "buttons", items: [zh ? "产品图" : "Photo", zh ? "花型图" : "Pattern", zh ? "合成图" : "Composite"] },
        },
        {
          title: zh ? "一键 AI 抠图" : "One-click AI cutout",
          desc: zh
            ? "点图片旁的抠图按钮，约 5 秒去掉背景换成纯白底，适合放进报价单和邮件。"
            : "Click the cutout button next to a photo: the background is replaced with pure white in about 5 seconds — ideal for quotes and emails.",
          hint: zh ? "抠图只处理你在系统里上传的图，不会动 FileMaker 同步过来的原图。" : "Cutout only touches photos uploaded here, never FileMaker-synced originals.",
          route: "/products",
          screen: { kind: "buttons", items: [zh ? "AI 抠图" : "AI cutout", zh ? "还原" : "Revert", zh ? "放大" : "Zoom"] },
        },
        {
          title: zh ? "维护价格表" : "Maintain the price list",
          desc: zh
            ? "右侧价格表按尺寸一行行填：工厂成本、关税、退税、装箱率、箱规、毛重、40HQ 装柜量，售价自动算出。"
            : "The price list on the right takes one row per size: factory cost, duty, rebate, packing rate, carton size, gross weight and 40HQ quantity; prices compute themselves.",
          hint: zh ? "每行右边的箱子图标可以从历史合同一键导入装箱数据。" : "The carton icon on each row imports carton data from past contracts.",
          route: "/products",
          screen: {
            kind: "table",
            head: [zh ? "尺寸" : "Size", zh ? "成本" : "Cost", zh ? "装箱率" : "Rate", zh ? "箱规" : "Carton"],
            rows: [["QUEEN", "¥17.51", "12", "48×30×32"], ["KING", "¥19.80", "12", "48×30×38"]],
            hit: 1,
          },
        },
        {
          title: zh ? "打标签方便复用" : "Tag it for reuse",
          desc: zh
            ? "给产品打上客户、季节或展会标签，之后在标签管理页可以整组挑出来加入报价单。"
            : "Tag the product by customer, season or fair; later you can pull the whole group into a quote from the Tags page.",
          hint: zh ? "标签可以共享给同事，对方也能在自己的标签列表看到。" : "Tags can be shared with colleagues, who then see them in their own list.",
          route: "/tags",
          screen: { kind: "buttons", items: ["ALPEMUSA", "SS26", zh ? "广交会" : "Canton Fair"] },
        },
        {
          title: zh ? "保存并检查同步" : "Save and check the sync",
          desc: zh
            ? "保存后，描述、图片和标签会在下一次同步写回 FileMaker；其他字段以 FileMaker 为准。"
            : "After saving, descriptions, photos and tags are pushed back to FileMaker on the next sync; FileMaker owns the other fields.",
          hint: zh ? "急着要同步？管理员可以在设置页点手动同步。" : "In a hurry? An admin can trigger the sync manually in Settings.",
          route: "/settings",
          screen: { kind: "buttons", items: [zh ? "保存" : "Save", zh ? "立即同步" : "Sync now"] },
        },
      ],
    },
  };
};
